import React, { useContext, useState, useEffect } from "react";
import {
  useParams,
  useHistory,
  useRouteMatch,
  NavLink,
} from "react-router-dom";

import useQuery from "../../../hooks/useQuery";

import { motion, AnimatePresence } from "framer-motion";
import ColourContext from "../../../contexts/ColourContext";
import { colourThemes } from "../../../styles/colourPalettes";
import lifeAreasData from "../../../resources/lifeAreas";

import userIcon from "../../../assets/icons/topNav/profile.svg";
// import notificationIcon from "../../../assets/icons/topNav/bell.svg";
import menuIcon from "../../../assets/icons/topNav/menu.svg";
import backIcon from "../../../assets/icons/arrow-left.svg";
import settingsIcons from "../../../assets/icons/settings.svg";

import style from "./TopNav.module.css";
import returnLifeAreaTitle from "../../../hooks/returnLifeAreaTitle";
import LifeAreasContext from "../../../contexts/LifeAreasContext";

type TopNavProps = {
  // notificationsPressed: () => void;
  profilePressed: () => void;
  menuPressed: () => void;
};

// Transition
const sectionVariants = {
  initial: {
    opacity: 0,
    x: -320,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      when: "beforeChildren",
      ease: "easeInOut",
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    x: -320,
    transition: {
      // when: "afterChildren",
      ease: "easeInOut",
      duration: 0.6,
    },
  },
};

const areaVariants = {
  initial: {
    opacity: 0,
    x: 320,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      when: "beforeChildren",
      // staggerChildren: 0.1,
      ease: "easeInOut",
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    x: 320,
    transition: {
      // when: "afterChildren",
      ease: "easeInOut",
      duration: 0.6,
    },
  },
};

const topNavButtonVariants = {
  initial: { opacity: 0, y: -12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const TopNav = ({ menuPressed, profilePressed }: TopNavProps) => {
  const { key: colourKey } = useContext(ColourContext);
  const colour = colourThemes[colourKey];
  const { sectionKey } = useParams();

  const { lifeAreas } = useContext(LifeAreasContext);

  const [isArea, setIsArea] = useState(false);
  const [disableAreaSettings, setDisableAreaSettings] = useState(false);
  const [title, setTitle] = useState("");

  const history = useHistory();
  // const query = useQuery();

  const matchAreaKey = useRouteMatch({
    path: "/personal/:areaKey",
    strict: true,
    sensitive: true,
  });

  useEffect(() => {
    // Viewing a personal area (different navigation)
    if (matchAreaKey && matchAreaKey.params) {
      // console.log("matchAreaKey", matchAreaKey);
      const params = matchAreaKey.params as any;
      // console.log(params.areaKey);
      const areaTitle = returnLifeAreaTitle(params.areaKey);
      setIsArea(true);
      setTitle(areaTitle);
      if (!lifeAreas.visions[params.areaKey]) setDisableAreaSettings(true);
    } else {
      setIsArea(false);
      setTitle(sectionKey);
    }
  }, [sectionKey, matchAreaKey]);

  // Check if the vision has been updated to enable the settings button
  useEffect(() => {
    if (isArea && matchAreaKey) {
      const params = matchAreaKey.params as any;
      if (lifeAreas.visions[params.areaKey]) {
        console.log("Enable Area Settings Button");
        setDisableAreaSettings(false);
      } else if (!disableAreaSettings) {
        console.log("Disable Area Settings Button");
        setDisableAreaSettings(true);
      }
    }
  }, [isArea, matchAreaKey, lifeAreas]);

  const handleBackToAreasPressed = () => {
    history.push(`/personal`);
  };

  const handleAreaSettingsPressed = () => {
    if (disableAreaSettings) return;
    const url = `${matchAreaKey?.url}/system`;
    history.push(url);
  };

  // TODO: Disable Area Settings button if a Vision hasn't been entered.
  // const disableSystem = visionText.length === 0;

  return (
    <nav className={style.container} style={{ borderColor: colour }}>
      <AnimatePresence initial={false}>
        {/* exitBeforeEnter={true} */}
        {!isArea && (
          <motion.div
            key="section"
            initial="initial"
            animate="visible"
            exit="exit"
            variants={sectionVariants}
          >
            <motion.img
              role="button"
              src={userIcon}
              alt="Profile"
              key="profile"
              variants={topNavButtonVariants}
              onTap={profilePressed}
            />
            <span>{title}</span>
            <motion.img
              role="button"
              src={menuIcon}
              alt="Menu"
              key="menu"
              variants={topNavButtonVariants}
              onTap={menuPressed}
            />
          </motion.div>
        )}
        {/* Navigator for /personal/<area> */}
        {isArea && (
          <motion.div
            key="area"
            initial="initial"
            animate="visible"
            exit="exit"
            variants={areaVariants}
          >
            <motion.img
              key="back"
              role="button"
              src={backIcon}
              variants={topNavButtonVariants}
              onClick={handleBackToAreasPressed}
            />
            <span>{title}</span>
            <motion.button
              key="settings"
              variants={topNavButtonVariants}
              disabled={disableAreaSettings}
              onClick={handleAreaSettingsPressed}
            >
              <img
                alt="settings"
                src={settingsIcons}
                style={{ opacity: disableAreaSettings ? 0.4 : 1 }}
              />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default TopNav;
